import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@redpanda/ui';
import { useDialogClickOutside, useModalScrollLock } from '../hooks';

interface DefaultAccount {
  code: string;
  name: string;
  type: string;
  description: string;
  parentCode?: string | null;
}

interface AccountInitializationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInitialize: (selectedAccounts: DefaultAccount[]) => Promise<void>;
  isLoading?: boolean;
}

// 預設會計科目列表（與後端保持一致）
const DEFAULT_ACCOUNTS: DefaultAccount[] = [
  // 資產類 (1xxx)
  { code: '1101', name: '現金', type: 'ASSET', description: '庫存現金及銀行存款' },
  { code: '1102', name: '銀行存款', type: 'ASSET', description: '各銀行存款帳戶' },
  { code: '1103', name: '零用金', type: 'ASSET', description: '日常開支零用金' },
  { code: '1201', name: '應收帳款', type: 'ASSET', description: '客戶應付款項' },
  { code: '1202', name: '應收票據', type: 'ASSET', description: '客戶開立票據' },
  { code: '1203', name: '其他應收款', type: 'ASSET', description: '其他應收款項' },
  { code: '1301', name: '存貨', type: 'ASSET', description: '商品、原料及製成品' },
  { code: '1302', name: '預付款項', type: 'ASSET', description: '預付費用及款項' },
  { code: '1501', name: '固定資產', type: 'ASSET', description: '不動產、廠房及設備' },
  { code: '1502', name: '累計折舊', type: 'ASSET', description: '固定資產累計折舊', parentCode: '1501' },

  // 負債類 (2xxx)
  { code: '2101', name: '應付帳款', type: 'LIABILITY', description: '供應商應付款項' },
  { code: '2102', name: '應付票據', type: 'LIABILITY', description: '供應商票據' },
  { code: '2103', name: '其他應付款', type: 'LIABILITY', description: '其他應付款項' },
  { code: '2201', name: '應付薪資', type: 'LIABILITY', description: '員工薪資及津貼' },
  { code: '2202', name: '應付稅款', type: 'LIABILITY', description: '各項應付稅款' },
  { code: '2203', name: '預收款項', type: 'LIABILITY', description: '客戶預付款' },
  { code: '2401', name: '長期借款', type: 'LIABILITY', description: '銀行長期借款' },

  // 權益類 (3xxx)
  { code: '3101', name: '股本', type: 'EQUITY', description: '實收股本' },
  { code: '3201', name: '保留盈餘', type: 'EQUITY', description: '累計未分配盈餘' },
  { code: '3301', name: '本期損益', type: 'EQUITY', description: '本期淨利或淨損' },

  // 收入類 (4xxx)
  { code: '4101', name: '營業收入', type: 'INCOME', description: '主要營業收入' },
  { code: '4102', name: '服務收入', type: 'INCOME', description: '服務業收入' },
  { code: '4201', name: '利息收入', type: 'INCOME', description: '銀行存款利息' },
  { code: '4202', name: '其他收入', type: 'INCOME', description: '非營業收入' },

  // 費用類 (5xxx)
  { code: '5101', name: '銷貨成本', type: 'EXPENSE', description: '直接成本' },
  { code: '5201', name: '薪資費用', type: 'EXPENSE', description: '員工薪資支出' },
  { code: '5202', name: '租金費用', type: 'EXPENSE', description: '租金支出' },
  { code: '5203', name: '水電費', type: 'EXPENSE', description: '水電瓦斯費用' },
  { code: '5204', name: '電話費', type: 'EXPENSE', description: '通信費用' },
  { code: '5205', name: '文具用品', type: 'EXPENSE', description: '辦公用品費用' },
  { code: '5206', name: '郵電費', type: 'EXPENSE', description: '郵寄運費' },
  { code: '5207', name: '旅費交通', type: 'EXPENSE', description: '出差交通費' },
  { code: '5208', name: '修繕費', type: 'EXPENSE', description: '設備維修費' },
  { code: '5209', name: '保險費', type: 'EXPENSE', description: '各項保險費用' },
  { code: '5210', name: '廣告費', type: 'EXPENSE', description: '廣告宣傳費用' },
  { code: '5211', name: '折舊費用', type: 'EXPENSE', description: '固定資產折舊' },
  { code: '5301', name: '利息費用', type: 'EXPENSE', description: '借款利息支出' },
  { code: '5401', name: '稅捐', type: 'EXPENSE', description: '各項稅捐費用' }
];

const AccountInitializationDialog: React.FC<AccountInitializationDialogProps> = ({
  isOpen,
  onClose,
  onInitialize,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set(DEFAULT_ACCOUNTS.map(acc => acc.code)));

  // 鎖定背景滾動
  useModalScrollLock(isOpen);

  const handleClose = () => {
    setSelectedAccounts(new Set(DEFAULT_ACCOUNTS.map(acc => acc.code))); // 重置為全選
    onClose();
  };

  // 點擊外部關閉 dialog
  const handleBackgroundClick = useDialogClickOutside(handleClose);

  const handleToggleAccount = (code: string) => {
    const newSelected = new Set(selectedAccounts);
    if (newSelected.has(code)) {
      newSelected.delete(code);
    } else {
      newSelected.add(code);
    }
    setSelectedAccounts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedAccounts.size === DEFAULT_ACCOUNTS.length) {
      setSelectedAccounts(new Set());
    } else {
      setSelectedAccounts(new Set(DEFAULT_ACCOUNTS.map(acc => acc.code)));
    }
  };

  const handleInitialize = async () => {
    const accountsToCreate = DEFAULT_ACCOUNTS.filter(acc => selectedAccounts.has(acc.code));
    await onInitialize(accountsToCreate);
  };

  // 按類型分組
  const accountsByType = DEFAULT_ACCOUNTS.reduce((groups, account) => {
    if (!groups[account.type]) {
      groups[account.type] = [];
    }
    groups[account.type].push(account);
    return groups;
  }, {} as Record<string, DefaultAccount[]>);

  const typeOrder = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 對話框標題 */}
        <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('accounts.initDialog.title')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 對話框內容 */}
        <div className="p-6">
          <div className="space-y-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {t('accounts.initDialog.description')}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="px-3 py-1 text-sm font-medium text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-200 border border-brand-200 dark:border-brand-700 rounded"
                >
                  {selectedAccounts.size === DEFAULT_ACCOUNTS.length ? t('bulk.deselectAll') : t('bulk.selectAll')}
                </button>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {t('accounts.initDialog.selectedCount', { count: selectedAccounts.size, total: DEFAULT_ACCOUNTS.length })}
                </div>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto space-y-4">
              {typeOrder.map(type => (
                <div key={type} className="space-y-2">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-slate-600 pb-1">
                    {t('accounts.initDialog.typeLabel', { type: t(`accounts.types.${type}`) })}
                  </h3>
                  <div className="grid gap-1">
                    {accountsByType[type]?.map(account => (
                      <div
                        key={account.code}
                        className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-slate-700"
                      >
                        <input
                          type="checkbox"
                          checked={selectedAccounts.has(account.code)}
                          onChange={() => handleToggleAccount(account.code)}
                          className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-slate-300 rounded flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
                              {account.code}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {account.name}
                            </span>
                            {account.parentCode && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {t('accounts.initDialog.subAccount')}
                              </span>
                            )}
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                              {account.description}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 對話框動作按鈕 */}
        <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 border border-gray-300 dark:border-gray-500 rounded-md"
          >
            {t('accounts.initDialog.cancel')}
          </button>
          <LoadingButton
            type="button"
            loading={isLoading}
            disabled={selectedAccounts.size === 0}
            onClick={handleInitialize}
            className="inline-flex justify-center rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading ? t('accounts.initDialog.initializing') : t('accounts.initDialog.initialize', { count: selectedAccounts.size })}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};

export default AccountInitializationDialog;